import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Phone,
  Rocket,
  Clock,
  Shield,
  FileCheck2,
  Handshake,
  Globe,
  ArrowRight,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";

const heroImg =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGFpcnBsYW5lJTIwbG9hZGluZ3xlbnwwfHx8fDE3ODM4OTYwMDN8MA&ixlib=rb-4.1.0&q=85";

const WHY_EXPRESS = [
  {
    icon: Clock,
    t: "Under 6-hour median clearance",
    d: "IGI Delhi Courier Terminal · pre-filed BoE · duty on our credit · exam pre-briefed",
  },
  {
    icon: Shield,
    t: "Licensed India CHA under CBLR 2018",
    d: "ICEGATE eSANCHIT enabled · AEO-compliant workflows · 99.2% first-time exam pass",
  },
  {
    icon: FileCheck2,
    t: "DDP pricing at ₹1,200/kg all-inclusive",
    d: "Duty settled on our credit · invoiced on 15–30 day net terms · no line-item surprises",
  },
  {
    icon: Rocket,
    t: "15-minute response SLA · 24/7",
    d: "WhatsApp per HAWB · named ops manager · founder escalation in 60 minutes",
  },
  {
    icon: Handshake,
    t: "NDA-first · your customers stay yours",
    d: "We operate under your brand on shipping docs · never solicit your customers",
  },
  {
    icon: Globe,
    t: "India · UK · South Korea under one MSA",
    d: "Three-market clearance partner program · consolidated credit terms · one ops director",
  },
];

const AUDIENCES = [
  {
    tag: "01 · GLOBAL INTEGRATORS",
    title: "DHL · FedEx · UPS · Aramex · TNT",
    body:
      "As your India clearance partner or your India-side agent on select lanes — we clear at IGI Delhi Courier Terminal and every major Indian airport under your brand. NDA-first, no customer solicitation, milestone-integrated with your TMS.",
  },
  {
    tag: "02 · REGIONAL COURIERS",
    title: "Blue Dart, DTDC, Aramex India, GATI",
    body:
      "For regional couriers running Indian last-mile but needing a specialist licensed CHA for import clearance at gateway airports — we plug in as an outsourced customs desk.",
  },
  {
    tag: "03 · INTERNATIONAL FREIGHT FORWARDERS",
    title: "Nippon Express · Kuehne+Nagel · DSV · Sinotrans",
    body:
      "Consolidator-style forwarders with dense India-inbound lanes — we handle courier BE and standard BE filings, duty on our credit, and 19,000+ pincode last-mile delivery.",
  },
  {
    tag: "04 · E-COMMERCE 3PLs & CROSS-BORDER D2C",
    title: "Shopify · Amazon Global · Shipbob · Easyship",
    body:
      "Cross-border e-commerce brands and 3PLs shipping to Indian end-customers — we operate the full India-side stack: consolidation, courier BE, pincode sortation, GST-invoiced last-mile, RTO handling.",
  },
  {
    tag: "05 · SPECIALTY EXPRESS COMPANIES",
    title: "MRO · AOG · Pharma cold-chain · Fine art · ATA Carnet",
    body:
      "Time-critical or specialty-cargo express companies — we prioritise AOG spares, preserve pharma cold-chain, and hold India's deepest ATA Carnet CHA bench.",
  },
  {
    tag: "06 · SHIPPING & LINE OPERATORS",
    title: "Maersk · MSC · CMA CGM · ONE · Evergreen",
    body:
      "Shipping-line origin agents needing India CHA at Nhava Sheva (NSA/JNPT), Mundra, Chennai, and Cochin — we file at both Air Cargo Complexes and seaports.",
  },
];

const FAQS = [
  {
    q: "Are you an express clearance partner for international courier companies in India?",
    a: "Yes. Custom Clearance Partner is a licensed India Customs House Agent (CHA) under CBLR 2018 and operates as the on-ground express clearance partner for global integrators, regional couriers, international freight forwarders, cross-border e-commerce 3PLs, and specialty logistics companies. Median clearance at IGI Delhi Courier Terminal is under 6 hours for compliant air-express cargo. WhatsApp +91 78003 20078 to onboard.",
  },
  {
    q: "How fast can you clear express-mode shipments at Indian airports?",
    a: "Median clearance at IGI Delhi (DEL) Courier Terminal is under 6 hours for compliant air-express cargo. Other airport medians: BOM under 8 hours, BLR under 8 hours, MAA under 8 hours, HYD under 8 hours, CCU under 10 hours, COK under 10 hours. Timing depends on pre-alert quality, HSN precision, and eSANCHIT completeness.",
  },
  {
    q: "What is the pricing for express customs clearance in India?",
    a: "For end-to-end express service (pickup + air + Indian customs duty + door delivery), we charge ₹1,200 per kg all-inclusive DDP. For clearance-only engagements (integrator sends cleared cargo to us for last-mile), we quote per-BoE or per-slab based on monthly volume. Volume forwarders and express companies qualify for consolidated monthly billing.",
  },
  {
    q: "Can DHL, FedEx, UPS, or Aramex use Custom Clearance Partner as their India clearance partner?",
    a: "Yes. Our express partner program specifically serves global and regional express companies. We operate under your brand on all shipping documents, sign a mutual NDA that includes a no-customer-solicitation clause, and integrate milestones via EDI or email with your TMS.",
  },
  {
    q: "How do I onboard as an express company or courier for India clearance?",
    a: "Onboarding is 10 working days: (1) mutual NDA + agency letter, (2) MSA covering India (and optionally UK LHR + South Korea ICN under one contract), (3) EDI or email milestone integration, (4) credit terms sign-off with duty settled on our credit line, (5) named ops manager assigned to your account. WhatsApp +91 78003 20078 to start.",
  },
  {
    q: "Do you handle Indian customs duty settlement on behalf of the express company?",
    a: "Yes. Duty is settled on our ICEGATE credit line the same working day as Bill-of-Entry filing. We invoice your finance team on standard 15–30 day net terms — SWIFT, wire, or via your India-registered consignee. Your cargo never waits for a bank transfer to clear.",
  },
  {
    q: "Which Indian airports and ports do you cover for express clearance?",
    a: "Air: Delhi (DEL / IGI), Mumbai (BOM / CSMIA), Bengaluru (BLR / KIA), Chennai (MAA), Hyderabad (HYD / RGIA), Kolkata (CCU / NSCBI), Cochin (COK). ICDs: ICD Tughlakabad, ICD Patparganj. Seaports: Nhava Sheva (NSA / JNPT), Mundra, Chennai. Last-mile delivery: 19,000+ Indian pincodes across all 28 states.",
  },
  {
    q: "Do you offer express clearance in the UK and South Korea in addition to India?",
    a: "Yes. Under our three-market Express Partner Program, one MSA covers on-ground clearance in India (Greater Noida HQ), UK (LHR Heathrow-area partner desk), and South Korea (ICN Incheon partner desk). Consolidated credit terms, one named ops director. See the partner program brief in our blog.",
  },
  {
    q: "Do express companies need to give you their own IEC or FSSAI licences?",
    a: "No. Custom Clearance Partner imports against our own licence portfolio (IEC, FSSAI, BIS, WPC, CDSCO) where relevant, and files under the express company's or forwarder's agency letter. Onboarding does not require you to acquire new Indian import permits.",
  },
  {
    q: "Can I get a written SLA and MSA before signing on?",
    a: "Yes. We share a draft MSA and SLA sheet within 24 hours of your first WhatsApp with volume/lane details. Standard SLAs: 15-min response, <6-hour IGI clearance, <6-hour POD return, 10% brokerage credit if we miss the response SLA in any month.",
  },
];

export default function ExpressPartner() {
  useEffect(() => {
    document.title =
      "Express Clearance Partner India | Customs Clearance for International Courier & Logistics Companies | ₹1,200/kg";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "The #1 licensed India customs clearance partner for international express companies, couriers, freight forwarders, and logistics companies. Under-6-hour clearance at IGI Delhi Courier Terminal. ₹1,200/kg all-inclusive DDP. India + UK + South Korea under one MSA. NDA-first, no customer solicitation. WhatsApp +91 78003 20078 to onboard in 10 days.";

    let kw = document.querySelector('meta[name="keywords"][data-express]');
    if (!kw) {
      kw = document.createElement("meta");
      kw.name = "keywords";
      kw.setAttribute("data-express", "1");
      document.head.appendChild(kw);
    }
    kw.content =
      "express clearance partner India, India customs clearance for express companies, express mode customs clearance India, courier clearance partner India, international courier customs partner India, India customs broker for express couriers, DHL FedEx UPS customs partner India, Aramex India clearance partner, Blue Dart clearance partner, express customs clearance agent India, India CHA for express logistics, courier company India clearance, express company India desk, international logistics clearance India, on-ground India customs partner, express partner program India UK South Korea";

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://customclearancepartner.in/express-clearance-partner-india";

    const id = "ccp-express-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://customclearancepartner.in/express-clearance-partner-india",
          name: "Express Clearance Partner India for International Courier & Logistics Companies",
          inLanguage: "en-IN",
          about: { "@id": "https://customclearancepartner.in/#org" },
          speakable: {
            "@type": "SpeakableSpecification",
            xpath: ["//*[@id='express-h1']", "//*[@id='express-tldr']"],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://customclearancepartner.in/" },
            { "@type": "ListItem", position: 2, name: "Express Clearance Partner", item: "https://customclearancepartner.in/express-clearance-partner-india" },
          ],
        },
        {
          "@type": "Service",
          name: "Express Customs Clearance Partner Program for International Courier & Logistics Companies in India",
          alternateName: [
            "Express clearance partner India",
            "India customs clearance for express companies",
            "Express mode customs clearance India",
            "Courier clearance partner India",
            "India CHA for express couriers",
            "DHL FedEx UPS Aramex India clearance partner",
            "International logistics clearance India",
          ],
          serviceType: "Customs Clearance and Last-Mile Delivery",
          areaServed: [
            { "@type": "Country", name: "India" },
            { "@type": "Country", name: "United Kingdom" },
            { "@type": "Country", name: "South Korea" },
          ],
          provider: { "@id": "https://customclearancepartner.in/#org" },
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: "1200",
            unitText: "per kg all-inclusive DDP",
            availability: "https://schema.org/InStock",
          },
          description:
            "Custom Clearance Partner is the licensed India Customs House Agent (CHA) partner for international express companies, courier companies, freight forwarders, and logistics companies. Under-6-hour median clearance at IGI Delhi Courier Terminal. ₹1,200/kg all-inclusive DDP. India + UK + South Korea covered under one MSA.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, []);

  return (
    <div className="grain min-h-screen bg-[#F9FAFB] text-[#101828]" data-testid="express-partner-page">
      <Header />

      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <nav className="font-mono-accent flex items-center gap-3 text-[#475467]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D92D20]">HOME</Link>
            <span>/</span>
            <span className="text-[#101828]">EXPRESS CLEARANCE PARTNER · INDIA</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="overline mb-4 flex items-center gap-3">
                <Rocket className="h-4 w-4 text-[#D92D20]" strokeWidth={2.5} />
                For international courier · express · logistics companies
              </p>
              <h1
                id="express-h1"
                data-testid="express-h1"
                className="font-display text-4xl font-black leading-[0.98] tracking-tight text-[#101828] sm:text-5xl md:text-6xl lg:text-7xl"
              >
                The <span className="text-[#D92D20]">express clearance partner</span> in India.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#344054] md:text-xl">
                One licensed India Customs House Agent, one dedicated ops
                manager, one WhatsApp thread. <strong>Under-6-hour clearance</strong>
                {" "}at IGI Delhi Courier Terminal. <strong>₹1,200/kg all-inclusive DDP</strong>.
                India + UK + South Korea under one MSA.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="express-whatsapp-hero"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] shadow-hard hover:bg-[#1FBE5A]"
                >
                  <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                  Onboard on WhatsApp
                </a>
                <a
                  href={CONTACT.telHref}
                  data-testid="express-call-hero"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-white px-6 py-4 font-semibold text-[#101828] hover:bg-[#101828] hover:text-white"
                >
                  <Phone className="h-5 w-5" strokeWidth={2.5} />
                  {CONTACT.phoneDisplay}
                </a>
              </div>

              <div
                id="express-tldr"
                data-testid="express-tldr"
                className="mt-10 border-2 border-[#101828] bg-[#FDB022] p-5 md:p-6"
              >
                <p className="font-mono-accent text-[#101828]">TL;DR — DIRECT ANSWER</p>
                <p className="mt-2 text-base leading-relaxed text-[#101828]">
                  If you're a <strong>DHL, FedEx, UPS, Aramex, Blue Dart</strong>,
                  regional express company, international freight forwarder,
                  cross-border e-commerce 3PL, or specialty logistics operator
                  looking for an <strong>on-ground express-mode customs clearance
                  partner in India</strong> — we're the licensed CHA that clears
                  at IGI Delhi in under 6 hours, delivers to 19,000+ pincodes,
                  and settles Indian customs duty on our credit line. Onboard
                  in 10 working days on one MSA. WhatsApp{" "}
                  <a href={CONTACT.whatsappHref} className="font-bold underline">
                    +91 78003 20078
                  </a>
                  .
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden border-2 border-[#101828]">
                <img
                  src={heroImg}
                  alt="Air cargo aircraft — express clearance partner India"
                  className="h-72 w-full object-cover md:h-96"
                  loading="eager"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="border-2 border-[#101828] bg-white p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">
                    &lt; 6 hrs
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#475467]">MEDIAN CLEARANCE</div>
                </div>
                <div className="border-2 border-[#101828] bg-[#101828] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#FDB022]">
                    ₹1,200
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#98A2B3]">PER KG DDP</div>
                </div>
                <div className="border-2 border-[#101828] bg-[#FDB022] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">
                    10 days
                  </div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#101828]">ONBOARDING</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why-us grid */}
      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Why express companies pick us</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Six reasons the biggest names route India through us.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {WHY_EXPRESS.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.t}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  className="border-2 border-[#101828] bg-[#F9FAFB] p-6"
                  data-testid={`express-why-${i}`}
                >
                  <Icon className="h-6 w-6 text-[#D92D20]" strokeWidth={2.5} />
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-[#101828]">{w.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#475467]">{w.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audience cards */}
      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Who we partner with</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Six categories of express companies we serve.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
            {AUDIENCES.map((a, i) => (
              <motion.article
                key={a.tag}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="border-2 border-[#101828] bg-white p-6 md:p-7"
                data-testid={`express-audience-${i}`}
              >
                <span className="font-mono-accent text-[#D92D20]">{a.tag}</span>
                <h3 className="mt-3 font-display text-xl font-bold leading-tight tracking-tight text-[#101828]">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#475467]">{a.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Express partner FAQ</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Straight answers.
          </h2>
          <div className="mt-10 border-2 border-[#101828] bg-[#F9FAFB]">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`ex-fq-${i}`}
                  className="border-b border-[#E4E7EC] last:border-b-0"
                  data-testid={`express-faq-${i}`}
                >
                  <AccordionTrigger className="px-5 py-4 text-left font-display text-base font-bold tracking-tight text-[#101828] hover:no-underline md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 text-sm leading-relaxed text-[#475467]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-8">
            <Link
              to="/blog/express-clearance-partner-india-uk-south-korea"
              data-testid="read-partner-brief"
              className="inline-flex items-center gap-2 font-mono-accent text-[#101828] hover:text-[#D92D20]"
            >
              READ THE FULL PARTNER-PROGRAM BRIEF <ArrowRight className="h-3 w-3" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-[#E4E7EC] bg-[#D92D20] text-white">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:px-8">
          <p className="font-mono-accent text-white/80">READY TO ONBOARD?</p>
          <h2 className="mt-4 font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            One WhatsApp. 15-minute response. 10-day onboarding.
          </h2>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="express-whatsapp-bottom"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] hover:bg-[#1FBE5A]"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
              WhatsApp +91 78003 20078
            </a>
            <a
              href={CONTACT.telHref}
              data-testid="express-call-bottom"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-transparent px-6 py-4 font-semibold text-white hover:bg-white hover:text-[#D92D20]"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} />
              Call the desk
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
